import { useCallback, useEffect, useRef, useState } from "react";
import { FileText, RotateCcw, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";

// Regole allineate al backend (family_documenti_service): estensioni ammesse e 50 MB per file.
export const ALLOWED_EXTENSIONS = [".pdf", ".jpg", ".jpeg", ".png", ".heic", ".heif", ".doc", ".docx", ".xls", ".xlsx"];
export const MAX_FILE_BYTES = 50 * 1024 * 1024;
export const MAX_BATCH_FILES = 10;
export const MAX_BATCH_BYTES = 200 * 1024 * 1024;

export function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1).replace(".", ",")} MB`;
}

export function validateFile(file) {
  const name = String(file?.name || "");
  const ext = name.includes(".") ? `.${name.split(".").pop().toLowerCase()}` : "";
  if (!ALLOWED_EXTENSIONS.includes(ext)) return "Formato non supportato (PDF, immagini, Word o Excel)";
  if (!file.size) return "Il file è vuoto";
  if (file.size > MAX_FILE_BYTES) return "Supera il limite di 50 MB";
  return "";
}

function apiError(err, fallback) {
  const detail = err?.response?.data?.detail;
  return Array.isArray(detail) ? detail.map((item) => item.msg || item).join(" ") : (typeof detail === "string" && detail) || fallback;
}

let counter = 0;
const makeItem = (file) => ({ key: `up-${++counter}`, file, status: "pending", progress: 0, error: "" });

/**
 * Coda di invio documenti. Riusa l'endpoint singolo POST /ospiti/{id}/documenti: ogni file passa dalle stesse
 * regole del backend; un errore non blocca gli altri; i falliti si possono riprovare.
 */
export function useMultiUpload({ api, ospiteId, onFinished }) {
  const [batch, setBatch] = useState(null); // { richiestaId, sostituisceFileId, items }
  const [running, setRunning] = useState(false);
  const controllers = useRef(new Map());
  const cancelled = useRef(false);
  const batchRef = useRef(null);
  batchRef.current = batch;

  const patch = useCallback((key, changes) => {
    setBatch((current) => (current ? { ...current, items: current.items.map((item) => (item.key === key ? { ...item, ...changes } : item)) } : current));
  }, []);

  const addFiles = useCallback((fileList, context = {}) => {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;
    setBatch((current) => {
      const sameContext = current && current.richiestaId === (context.richiestaId || null);
      const base = sameContext ? current.items : [];
      const items = [...base];
      let total = items.reduce((sum, item) => sum + (item.status === "invalid" ? 0 : item.file.size), 0);
      let count = items.filter((item) => item.status !== "invalid").length;
      let skipped = 0;
      incoming.forEach((file) => {
        const problem = validateFile(file);
        const item = makeItem(file);
        if (problem) { item.status = "invalid"; item.error = problem; items.push(item); return; }
        if (count >= MAX_BATCH_FILES) { skipped += 1; return; }
        if (total + file.size > MAX_BATCH_BYTES) { item.status = "invalid"; item.error = "Supera la dimensione totale consentita (200 MB)"; items.push(item); return; }
        count += 1; total += file.size; items.push(item);
      });
      if (skipped) toast.error(`Puoi inviare al massimo ${MAX_BATCH_FILES} file alla volta: ${skipped} non ${skipped === 1 ? "aggiunto" : "aggiunti"}`);
      return {
        richiestaId: context.richiestaId || null,
        sostituisceFileId: sameContext ? current.sostituisceFileId : (context.sostituisceFileId || null),
        items,
      };
    });
  }, []);

  const remove = useCallback((key) => {
    controllers.current.get(key)?.abort();
    setBatch((current) => {
      if (!current) return current;
      const items = current.items.filter((item) => item.key !== key);
      return items.length ? { ...current, items } : null;
    });
  }, []);

  const clear = useCallback(() => { setBatch(null); }, []);

  const run = useCallback(async (only) => {
    const current = batchRef.current;
    if (!current || running) return;
    const onlyKeys = Array.isArray(only) ? only : null;
    const todo = current.items
      .filter((item) => ["pending", "error", "cancelled"].includes(item.status) && (!onlyKeys || onlyKeys.includes(item.key)))
      .map((item) => item.key);
    if (!todo.length) return;
    cancelled.current = false;
    setRunning(true);
    todo.forEach((key) => patch(key, { status: "pending", error: "", progress: 0 }));
    const queue = [...todo];
    const outcome = { ok: 0, failed: 0, cancelled: 0, lastError: "" };
    // il primo file di una risposta "da integrare" sostituisce il documento precedente; gli altri sono aggiunte
    let replaceUsed = false;
    const worker = async () => {
      while (queue.length && !cancelled.current) {
        const key = queue.shift();
        const item = (batchRef.current?.items || []).find((entry) => entry.key === key);
        if (!item) continue;
        const body = new FormData();
        body.append("file", item.file);
        if (current.richiestaId) body.append("richiesta_id", current.richiestaId);
        if (current.sostituisceFileId && !replaceUsed) { body.append("sostituisce_file_id", current.sostituisceFileId); replaceUsed = true; }
        const controller = new AbortController();
        controllers.current.set(key, controller);
        patch(key, { status: "uploading", progress: 0 });
        try {
          await api.post(`/ospiti/${ospiteId}/documenti`, body, {
            signal: controller.signal,
            onUploadProgress: (event) => {
              if (event?.total) patch(key, { progress: Math.min(99, Math.round((event.loaded / event.total) * 100)) });
            },
          });
          patch(key, { status: "done", progress: 100 });
          outcome.ok += 1;
        } catch (err) {
          if (cancelled.current || controller.signal.aborted) {
            patch(key, { status: "cancelled", progress: 0, error: "Invio annullato" });
            outcome.cancelled += 1;
          } else {
            outcome.lastError = apiError(err, "Non è stato possibile inviare il documento");
            patch(key, { status: "error", error: outcome.lastError, progress: 0 });
            outcome.failed += 1;
          }
        } finally {
          controllers.current.delete(key);
        }
      }
    };
    const concurrency = current.richiestaId ? 1 : 2;
    await Promise.all(Array.from({ length: Math.min(concurrency, todo.length) }, worker));
    // quanto non è partito a causa dell'annullamento torna "annullato"
    queue.forEach((key) => { patch(key, { status: "cancelled", error: "Invio annullato" }); outcome.cancelled += 1; });
    setRunning(false);
    const single = todo.length === 1;
    if (outcome.failed === 0 && outcome.cancelled === 0) {
      toast.success(single ? "Documento inviato alla struttura" : `${outcome.ok} documenti inviati alla struttura`);
      setBatch(null);
    } else if (outcome.cancelled && !outcome.failed) {
      toast.error(`Invio annullato: ${outcome.ok} ${outcome.ok === 1 ? "inviato" : "inviati"}, ${outcome.cancelled} non ${outcome.cancelled === 1 ? "inviato" : "inviati"}`);
    } else if (single) {
      toast.error(outcome.lastError);
    } else {
      toast.error(`${outcome.ok} inviati, ${outcome.failed + outcome.cancelled} non inviati: puoi riprovare quelli in errore`);
    }
    if (outcome.ok) onFinished?.();
  }, [api, ospiteId, onFinished, patch, running]);

  const cancelAll = useCallback(() => {
    cancelled.current = true;
    controllers.current.forEach((controller) => controller.abort());
  }, []);

  useEffect(() => () => { cancelled.current = true; controllers.current.forEach((controller) => controller.abort()); }, []);

  return { batch, running, addFiles, remove, clear, run, cancelAll };
}

/** Zona di rilascio/selezione: label con input multiplo e drag & drop. */
export function DropLabel({ children, onFiles, inputKey, ariaLabel, className = "" }) {
  const [over, setOver] = useState(false);
  const stop = (event) => { event.preventDefault(); event.stopPropagation(); };
  return (
    <label
      className={`${className} ${over ? "ring-2 ring-[#6e48b5]/50 bg-[#EDE9F6]/60" : ""}`}
      onDragEnter={(e) => { stop(e); setOver(true); }}
      onDragOver={(e) => { stop(e); setOver(true); }}
      onDragLeave={(e) => { stop(e); setOver(false); }}
      onDrop={(e) => { stop(e); setOver(false); onFiles(e.dataTransfer?.files); }}
    >
      {children}
      <input
        key={inputKey}
        className="sr-only"
        type="file"
        multiple
        accept={ALLOWED_EXTENSIONS.join(",")}
        aria-label={ariaLabel}
        onChange={(e) => { onFiles(e.target.files); e.target.value = ""; }}
      />
    </label>
  );
}

const STATUS_LABEL = { pending: "In attesa", uploading: "Invio in corso", done: "Inviato", error: "Errore", invalid: "Non valido", cancelled: "Annullato" };

export function UploadQueue({ queue, title }) {
  const { batch, running, remove, clear, run, cancelAll } = queue;
  if (!batch?.items?.length) return null;
  const items = batch.items;
  const pending = items.filter((item) => item.status === "pending").length;
  const retriable = items.filter((item) => item.status === "error" || item.status === "cancelled").length;
  const done = items.filter((item) => item.status === "done").length;
  const sendable = pending + retriable;
  const summary = running ? `Invio in corso: ${done} di ${items.filter((i) => i.status !== "invalid").length}` : `${items.length} ${items.length === 1 ? "file selezionato" : "file selezionati"}`;
  return (
    <section className="family-card p-4" aria-label="Documenti da inviare">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-extrabold text-[#304762]">{title || "Documenti da inviare"}</p>
        <p className="text-xs text-[#72839a]" role="status" aria-live="polite">{summary}</p>
      </div>
      <ul className="mt-3 space-y-2">
        {items.map((item) => (
          <li key={item.key} className="rounded-xl border border-slate-100 p-2.5" data-status={item.status}>
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 shrink-0 text-[#6e48b5]" />
              <p className="min-w-0 flex-1 truncate text-sm font-bold text-[#304762]">{item.file.name}</p>
              <span className="shrink-0 text-[11px] text-[#72839a]">{formatBytes(item.file.size)}</span>
              {(item.status === "error" || item.status === "cancelled") && !running ? (
                <button type="button" onClick={() => run([item.key])} className="rounded-lg px-2 py-0.5 text-[11px] font-bold text-[#6e48b5] hover:bg-[#EDE9F6]" aria-label={`Riprova ${item.file.name}`}>Riprova</button>
              ) : null}
              {!(running && item.status === "uploading") ? (
                <button type="button" onClick={() => remove(item.key)} className="rounded-full p-1 text-slate-400 hover:bg-slate-50 hover:text-slate-700" aria-label={`Rimuovi ${item.file.name}`}>
                  <X className="h-4 w-4" />
                </button>
              ) : null}
            </div>
            <div className="mt-1.5 flex items-center gap-2 pl-6">
              {item.status === "uploading" ? (
                <progress value={item.progress} max={100} aria-label={`Avanzamento ${item.file.name}`} className="h-1.5 w-full flex-1 overflow-hidden rounded-full" />
              ) : null}
              <span className={`text-[11px] font-semibold ${item.status === "error" || item.status === "invalid" ? "text-red-600" : item.status === "done" ? "text-emerald-600" : "text-[#72839a]"}`}>
                {STATUS_LABEL[item.status]}{item.error && item.status !== "cancelled" ? ` · ${item.error}` : ""}{item.status === "uploading" && item.progress ? ` · ${item.progress}%` : ""}
              </span>
            </div>
          </li>
        ))}
      </ul>
      <div className="mt-3 grid grid-cols-[1fr_auto] gap-2">
        {running ? (
          <>
            <Button disabled className="h-10 rounded-xl bg-[#6e48b5]"><Upload className="mr-2 h-4 w-4" />Caricamento…</Button>
            <Button type="button" variant="outline" onClick={cancelAll} className="h-10 rounded-xl">Annulla invio</Button>
          </>
        ) : (
          <>
            {sendable ? (
              <Button onClick={() => run()} className="h-10 rounded-xl bg-[#6e48b5] hover:bg-[#5a3b94]">
                {pending ? <Upload className="mr-2 h-4 w-4" /> : <RotateCcw className="mr-2 h-4 w-4" />}
                {pending ? (pending === 1 && !retriable ? "Invia documento" : `Invia ${pending + retriable} documenti`) : (retriable === 1 ? "Riprova invio" : `Riprova ${retriable} documenti`)}
              </Button>
            ) : <span />}
            <Button type="button" variant="outline" onClick={clear} className="h-10 rounded-xl">{sendable ? "Annulla" : "Chiudi"}</Button>
          </>
        )}
      </div>
    </section>
  );
}
